import { get, post, put, del } from 'aws-amplify/api';
import { ZodType as ZodSchema } from 'zod';

export type ApiErrorKind = 'network' | 'auth' | 'validation' | 'server' | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly statusCode?: number;

  constructor(message: string, kind: ApiErrorKind, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.statusCode = statusCode;
  }
}

function classifyStatusCode(status: number): ApiErrorKind {
  if (status === 401 || status === 403) return 'auth';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true; // fetch failures
  if (err instanceof DOMException && err.name === 'AbortError') return true; // timeouts
  return false;
}

/**
 * Normalise any item/record coming from the backend so that `itemId` is
 * always the canonical identifier regardless of which field the Lambda
 * happened to return (id, _id, pk, sk, noteId, goalId, …).
 */
export function normaliseItemId<T extends Record<string, any>>(item: T): T & { itemId: string } {
  const id = item.itemId ?? item.id ?? item._id ?? item.noteId ?? item.goalId ?? '';
  return { ...item, itemId: String(id) };
}

/** Normalise an array of items. */
export function normaliseItems<T extends Record<string, any>>(items: T[]): (T & { itemId: string })[] {
  return items.map(normaliseItemId);
}

interface SuccessEnvelope<T>{ success: true; data: T; requestId: string }
interface ErrorEnvelope { success: false; error: { code: string; message: string; details?: unknown }; requestId: string }

interface RequestConfig<T> {
  api: string;
  path: string;
  body?: any;
  headers?: Record<string,string>;
  schema?: ZodSchema<T>;
  raw?: boolean; // if true, bypass envelope unpacking
}

type HttpMethod = 'get' | 'post' | 'put' | 'del';
const methodFns = { get, post, put, del } as const;

async function call<T=unknown>(method: HttpMethod, config: RequestConfig<T>): Promise<T>{
  const { api: apiName, path, body, headers, schema, raw } = config;
  const options: any = {};
  if (body !== undefined) options.body = body;
  if (headers) options.headers = headers;

  let response: any;
  try {
    const operation = (methodFns[method] as any)({ apiName, path, options });
    response = await operation.response;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (isNetworkError(err)) {
      throw new ApiError((err as Error).message || 'Network request failed', 'network');
    }
    // Amplify may throw RestApiError with statusCode on non-2xx responses
    const statusCode = (err as any)?.response?.statusCode ?? (err as any)?.statusCode;
    if (typeof statusCode === 'number') {
      const kind = classifyStatusCode(statusCode);
      throw new ApiError((err as Error).message || `Request failed (${statusCode})`, kind, statusCode);
    }
    throw new ApiError((err as Error).message || 'Unknown request error', 'unknown');
  }

  let data: any = await response.body.json();

  if (!raw) {
    if (data && typeof data === 'object' && (data as SuccessEnvelope<T>).success === true) {
      data = (data as SuccessEnvelope<T>).data;
    } else if (data && typeof data === 'object' && (data as ErrorEnvelope).success === false) {
      const envelope = data as ErrorEnvelope;
      const statusCode = response.statusCode as number | undefined;
      const kind = statusCode ? classifyStatusCode(statusCode) : 'unknown';
      const err = new ApiError(envelope.error?.message || 'Request failed', kind, statusCode);
      (err as any).code = envelope.error?.code;
      throw err;
    }
  }
  if (schema) {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      const err = new ApiError('Response validation failed', 'validation');
      (err as any).issues = parsed.error.issues;
      throw err;
    }
    return parsed.data;
  }
  return data as T;
}

export const apiClient = {
  get: <T=unknown>(api: string, path: string, opts: Omit<RequestConfig<T>, 'api'|'path'> = {})=> call<T>('get', { api, path, ...opts }),
  post: <T=unknown>(api: string, path: string, opts: Omit<RequestConfig<T>, 'api'|'path'> = {})=> call<T>('post', { api, path, ...opts }),
  put: <T=unknown>(api: string, path: string, opts: Omit<RequestConfig<T>, 'api'|'path'> = {})=> call<T>('put', { api, path, ...opts }),
  del: <T=unknown>(api: string, path: string, opts: Omit<RequestConfig<T>, 'api'|'path'> = {})=> call<T>('del', { api, path, ...opts }),
};

export default apiClient;
